/**
 * The function formats a numeric value as a percentage string.
 * Rules:
 * 1. After conversion to percentage, by default rounds to 6 decimal places.
 * 2. For very small values smaller than 0.000001 (10^-6), automatically increases
 *    decimal places until the first significant digit appears.
 * 3. Returns a fixed decimal representation, never using scientific notation.
 * 4. Returns an empty string for `undefined`, `null`, `NaN`, or non-finite numbers.
 */
export const formatPercentValue = (value: number): string => {
  if (value === undefined || value === null || Number.isNaN(value)) {
    return '';
  }
  const percent = value * 100;
  if (!Number.isFinite(percent)) {
    return '';
  }
  const DEC = 6;
  // exact zero -> just "0"
  if (percent === 0) {
    return '0';
  }
  // Helper to trim trailing zeros and trailing dot
  const trimZeros = (str: string) => {
    if (str.includes('.')) {
      // remove trailing zeros, then remove trailing decimal point if any
      str = str.replace(/(\.\d*?[1-9])0+$/, '$1').replace(/\.0+$/, '');
    }
    return str;
  };
  // Not extremely small: keep 6 decimals then trim
  if (Math.abs(percent) >= Math.pow(10, -DEC)) {
    return trimZeros(percent.toFixed(DEC));
  }
  // Very small numbers: increase decimals until first significant digit appears
  for (let d = DEC; d <= 20; d++) {
    const s = percent.toFixed(d);
    if (parseFloat(s) !== 0) {
      return trimZeros(s); // never scientific notation
    }
  }
  return trimZeros(percent.toFixed(20));
};
